import React, { ChangeEvent, useCallback, useLayoutEffect, useRef } from 'react';

import { Search } from '@yandex-360/iconostasis/dist/icons/Search';
import { Textinput } from '@yandex-360/orb-components/dist/components/Textinput';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import Button from '../../../../shared/components/button';
import { ScrollContainer } from '../../../../shared/components/scroll-container';
import { SegmentedControl } from '../../../../shared/components/segmented-control';
import SvgImage from '../../../../shared/components/svg-image';
import { selectIsMeetingRoomIntegration } from '../../../../shared/features/config/selectors';
import { useScrollController } from '../../../../shared/features/scroll-controller';
import { getCssCustomProperty } from '../../../../shared/helpers/get-css-custom-property';
import { i18n } from '../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { AccessLevel } from '../../../../types';
import { PeersTabValue } from '../../constants';
import {
    selectParticipantSearchResult,
    selectParticipantsSearchQuery,
    updateQuery,
} from '../../features/participants-search';
import { participantsScrollController } from '../../features/sidebar/participants-scroll-controller';
import { setPeersTab, setSidebarContent } from '../../features/sidebar/slice';
import {
    getConferenceAccessLevel,
    getParticipantsCount,
    isAccessRestrictionOrganizationAllowedSelector,
    selectCanWatchWaitingRoom,
    selectConferenceIsFull,
    selectShouldShowNewWaitingRoomPeersIndicator,
    selectSidebarPeersTab,
    selectWaitingRoomIsEmpty,
    selectWaitingRoomPeersCount,
} from '../../selectors';
import AccessLevelControlContainer from '../containers/access-level-control-container';
import { ParticipantsList } from '../participants-list';
import { ParticipantsSearchResult } from '../participants-search-result';
import { WaitingRoomPeersList } from '../waiting-room-peers-list';
import { ParticipantsFooter } from './participants-footer';
import styles from './styles.css';
import { WaitingRoomPeersFooter } from './waiting-room-peers-footer';

const closeIcon = <SvgImage imageName="close2" />;

const METRIKA_NAMESPACE = ['waiting_rooms', 'waiting_list'];
export const ParticipantsSidebar = () => {
    const dispatch = useDispatch();
    const isMeetingRoomIntegration = useSelector(selectIsMeetingRoomIntegration);
    const isAccessRestrictionOrganizationAllowed = useSelector(isAccessRestrictionOrganizationAllowedSelector);
    const participantsCount = useSelector(getParticipantsCount);
    const waitingRoomPeersCount = useSelector(selectWaitingRoomPeersCount);
    const hasNewWaitingRoomPeers = useSelector(selectShouldShowNewWaitingRoomPeersIndicator);
    const conferenceIsFull = useSelector(selectConferenceIsFull);
    const canWatchWaitingRoom = useSelector(selectCanWatchWaitingRoom);
    const waitingRoomIsEmpty = useSelector(selectWaitingRoomIsEmpty);
    const currentAccessLevel = useSelector(getConferenceAccessLevel);

    const isWaitingRoomShouldHide = currentAccessLevel === AccessLevel.PUBLIC && waitingRoomIsEmpty;
    const isWaitingRoomVisible = canWatchWaitingRoom && !isWaitingRoomShouldHide;

    const onCloseButtonClick = useCallback(() => {
        countMetrika('meeting_screen', 'participants_list', 'close');
        dispatch(setSidebarContent(null));
    }, [dispatch]);

    const peersTab = useSelector(selectSidebarPeersTab);
    const isConferenceTab = peersTab === PeersTabValue.Conference;

    const changePeersTab = useCallback(
        (tab: PeersTabValue) => {
            dispatch(setPeersTab(tab));
            if (tab === PeersTabValue.WaitingRoom) {
                countMetrika(...METRIKA_NAMESPACE, 'tab_open');
            }
        },
        [dispatch]
    );

    const onPeersTabChange = (value: string) => {
        changePeersTab(value as PeersTabValue);
    };

    useLayoutEffect(() => {
        if (isWaitingRoomShouldHide) {
            changePeersTab(PeersTabValue.Conference);
        }
    }, [changePeersTab, isWaitingRoomShouldHide]);

    const participantsCountBadge = <div className={styles.participantsCount}>{participantsCount}</div>;
    const waitingRoomPeersCountBadge = (
        <div className={cn(styles.participantsCount, { [styles.hasNew]: hasNewWaitingRoomPeers })}>
            {waitingRoomPeersCount}
        </div>
    );

    const footer = isMeetingRoomIntegration ? null : isConferenceTab ? (
        <ParticipantsFooter />
    ) : conferenceIsFull || waitingRoomIsEmpty ? null : (
        <WaitingRoomPeersFooter />
    );

    const participantsSearchQuery = useSelector(selectParticipantsSearchQuery);
    const onSearchTextInputChanged = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            dispatch(updateQuery(event.target.value));
            if (event.target.value.length === 0) {
                /*
                    Сейчас обрита не поддерживает возможность узнать
                    как был очищен текстинпут (с клавиатуры или с кнопки clear)
                    завел тикет FRONTCORE-2344, чтобы они поддержали такую возможность.
                    Сейчас при очистке инпута по кнопке приходит урезанное событие
                    всего с 2 полями: target и currentTarget. Пока временно заюзал этот косвенный признак
                */
                const eventKeys = Object.keys(event);
                const isClearByButton =
                    eventKeys.length === 2 && eventKeys.includes('target') && eventKeys.includes('currentTarget');
                if (isClearByButton) {
                    countMetrika('meeting_screen', 'participants_list', 'reset_symbols');
                } else {
                    countMetrika('meeting_screen', 'participants_list', 'delete_symbols');
                }
            } else if (event.target.value.length === 1 && participantsSearchQuery.length === 0) {
                countMetrika('meeting_screen', 'participants_list', 'input_first_symbol');
            }
        },
        [dispatch, participantsSearchQuery.length]
    );

    const participantsSearchResult = useSelector(selectParticipantSearchResult);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    useScrollController({
        controller: participantsScrollController,
        scrollContainerRef,
        trackScrollState: isMeetingRoomIntegration,
    });

    return (
        <div className={styles.participantsSidebar}>
            {isMeetingRoomIntegration ? null : (
                <Button
                    className={styles.close}
                    view="neutral-transparent"
                    size="sm"
                    onClick={onCloseButtonClick}
                    icon={closeIcon}
                    aria-label={i18n('toolbar', 'close-toolbar')}
                />
            )}
            <div className={styles.head}>
                <h1 className={styles.title}>
                    {i18n('toolbar', 'participants-button-title')}
                    {!isWaitingRoomVisible && participantsCountBadge}
                </h1>
                {isAccessRestrictionOrganizationAllowed ? (
                    <AccessLevelControlContainer
                        currentAccessLevel={currentAccessLevel}
                        data-test-id="show-access-level-popup"
                        className={styles.accessLevelControl}
                    />
                ) : null}
                {!isMeetingRoomIntegration && (
                    <Textinput
                        className={styles.search}
                        startSlot={<Search size="sm" color={getCssCustomProperty('--orb-color-cool-gray-450')} />}
                        hasClear={true}
                        value={participantsSearchQuery}
                        onChange={onSearchTextInputChanged}
                        placeholder={i18n('common', 'name')}
                    />
                )}
                {isWaitingRoomVisible && participantsSearchResult === null ? (
                    <SegmentedControl
                        className={styles.peersTabs}
                        container="fixed"
                        options={[
                            {
                                value: PeersTabValue.Conference,
                                children: (
                                    <div data-test-id="conference-tab">
                                        {i18n('participants-sidebar', 'peers-tab-conference')}
                                        {participantsCountBadge}
                                    </div>
                                ),
                            },
                            {
                                value: PeersTabValue.WaitingRoom,
                                children: (
                                    <div data-test-id="waiting-room-tab">
                                        {i18n('participants-sidebar', 'peers-tab-waiting-room')}
                                        {waitingRoomPeersCountBadge}
                                    </div>
                                ),
                            },
                        ]}
                        value={peersTab}
                        onChange={onPeersTabChange}
                    />
                ) : null}
            </div>
            <ScrollContainer className={styles.content} ref={scrollContainerRef}>
                {participantsSearchResult !== null ? (
                    <ParticipantsSearchResult searchResult={participantsSearchResult} />
                ) : isConferenceTab ? (
                    <ParticipantsList />
                ) : (
                    <WaitingRoomPeersList />
                )}
            </ScrollContainer>
            {footer && participantsSearchResult === null ? <div className={styles.footer}>{footer}</div> : null}
        </div>
    );
};
